import { useState, useRef, useEffect } from 'react';

// Available roles - easy to extend in the future
export const AVAILABLE_ROLES = [
  { value: 'user', label: 'Member', description: 'Standard access' },
  { value: 'org_admin', label: 'Admin', description: 'Full organization access' },
] as const;

export type RoleValue = typeof AVAILABLE_ROLES[number]['value'];

interface RoleSelectorProps {
  currentRole: string;
  isUpdating: boolean;
  onRoleChange: (newRole: RoleValue) => void;
  disabled?: boolean;
}

export function RoleSelector({ currentRole, isUpdating, onRoleChange, disabled }: RoleSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const currentRoleInfo = AVAILABLE_ROLES.find(r => r.value === currentRole) || AVAILABLE_ROLES[0];

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (role: RoleValue) => {
    if (role !== currentRole) {
      onRoleChange(role);
    }
    setIsOpen(false);
  };

  const isDisabled = isUpdating || disabled;

  return (
    <div className="role-selector" ref={dropdownRef}>
      <button
        className={`role-selector-trigger ${currentRole === 'org_admin' ? 'admin' : 'user'}`}
        onClick={() => !isDisabled && setIsOpen(!isOpen)}
        disabled={isDisabled}
      >
        <span className="role-label">{isUpdating ? 'Updating...' : currentRoleInfo.label}</span>
        {!disabled && (
          <svg className="chevron" width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        )}
      </button>
      {isOpen && (
        <div className="role-selector-dropdown">
          {AVAILABLE_ROLES.map((role) => (
            <button
              key={role.value}
              className={`role-option ${role.value === currentRole ? 'selected' : ''}`}
              onClick={() => handleSelect(role.value)}
            >
              <div className="role-option-content">
                <span className="role-option-label">{role.label}</span>
                <span className="role-option-description">{role.description}</span>
              </div>
              {role.value === currentRole && (
                <svg className="check-icon" width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M11.5 4L5.5 10L2.5 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
